import { useCallback, useContext, useMemo } from 'react';

import { ColumnDef, createColumnHelper, SortingState, Updater } from '@tanstack/react-table';
import { compact, invert, mapValues } from 'lodash';

import { ListingField } from '../../__generated__/globalTypes';
import { restrictedResizeColumnIds } from '../../app-level/config/TableRestrictions';
import { DataContext } from '../../context/DataContext';
import { useFeatureFlagVersion } from '../../context/FeatureFlagContext';
import { useColumnOrder } from '../../hooks/useColumnOrder';
import { Alignment, DataDisplayTable, DataDisplayTableProps } from '../../ui-kit';
import { GetMarketplaceListings_listings as ListingType } from './__generated__/GetMarketplaceListings';
import DealLink from './DealLink';
import { ListingColsConfig, listingColsConfig, ListingKey } from './marketplace.config';

const tableColumns = (columnOrder: ListingKey[], bidsTableVersion: string | null) => {
  return [
    createColumnHelper<ListingType>().display({
      id: 'view-deal',
      cell: (props) => {
        const isMyListing = props.row.original.company.__typename === 'UserCompany';

        const getActiveTradesUrl = () => {
          return bidsTableVersion === 'v1'
            ? '/marketplace/active_trades/all'
            : '/marketplace/active_trades';
        };

        const listingHref = isMyListing
          ? getActiveTradesUrl()
          : `../listing/${props.row.original.id}`;

        return (
          <DealLink
            isMyListing={isMyListing}
            listingHref={listingHref}
            listingId={props.row.original.id}
            listingDetails={props.row.original}
            dealId={props.row.original.in_progress_deal_id}
          />
        );
      },
      meta: {
        headerNoPill: true,
      },
    }),
    ...getColumns(listingColsConfig, columnOrder),
  ] as ColumnDef<ListingType, ValueOf<ListingType>>[];
};

type MarketplaceTableProps = {
  data: ListingType[];
  selection?: DataDisplayTableProps<ListingType, unknown>['selection'];
  sorting: {
    state: { id: Partial<ListingField>; desc: boolean }[];
    onSortingChanged: (val: { id: ListingField; desc: boolean }[]) => void;
  };
};

export const MarketplaceTable: React.FC<MarketplaceTableProps> = (props) => {
  const { sorting } = props;
  const { state } = useContext(DataContext);
  const bidsTableVersion = useFeatureFlagVersion('bidsTable');

  const { setColumnOrder } = useColumnOrder(
    'marketHomeTable',
    state.globalTableOrderConfig.marketPlace.marketHomeTable,
  );

  const sortFieldsToColumns = listingColsConfig
    ? invert(mapValues(listingColsConfig, (entry) => entry?.sortSelector))
    : {};

  const sortingState = useMemo(
    () =>
      sorting?.state.map((entry) => ({
        ...entry,
        id: sortFieldsToColumns[entry.id],
      })),
    [sorting?.state, sortFieldsToColumns],
  );

  const handleSortingChanged = useCallback(
    (update: Updater<SortingState>) => {
      const val = typeof update === 'function' ? update(sortingState) : update;
      const sortVal: {
        id: ListingField;
        desc: boolean;
      }[] = compact(
        val.map((it) => {
          const sortSelector =
            listingColsConfig[it.id as keyof typeof listingColsConfig]?.sortSelector;
          return sortSelector
            ? {
                ...it,
                id: sortSelector as ListingField,
              }
            : null;
        }),
      );
      sorting?.onSortingChanged(sortVal);
    },
    [sortingState, listingColsConfig, sorting],
  );

  const sortingProp = useMemo(
    () =>
      sortingState
        ? {
            state: sortingState,
            onSortingChanged: handleSortingChanged,
            isManual: true,
          }
        : undefined,
    [sortingState, handleSortingChanged],
  );

  const columns = useMemo(
    () =>
      tableColumns(
        state.globalTableOrderConfig.marketPlace.marketHomeTable.filter(
          (item) => item !== 'view-deal',
        ) as ListingKey[],
        bidsTableVersion,
      ),
    [state.globalTableOrderConfig.marketPlace.marketHomeTable, bidsTableVersion],
  );

  return (
    <DataDisplayTable
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      data={props.data}
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      columns={columns}
      noDataMessage="No Data"
      selection={props.selection}
      sorting={sortingProp}
      isDraggable={true}
      resizeCfg={restrictedResizeColumnIds.marketPlace.marketHomeTable}
      setColumnOrder={setColumnOrder}
      columnOrder={state.globalTableOrderConfig.marketPlace.marketHomeTable}
    />
  );
};

type ValueOf<T> = T[keyof T];

function getColumns(colCfg: ListingColsConfig, columnOrder: ListingKey[]) {
  const columns: ColumnDef<ListingType, ValueOf<ListingType>>[] = columnOrder.map((col) => ({
    accessorKey: col,
    id: col,
    header: listingColsConfig[col].string,
    meta: {
      numeric: listingColsConfig[col].numeric,
      alignment: Alignment.left,
    },
    enableSorting: !!colCfg[col].sortSelector,
    cell: (props) => {
      const fieldValue = props.getValue() as ValueOf<ListingType>;
      const config = listingColsConfig[col];
      // @ts-expect-error - TS thinks display's param is never
      return config.display(fieldValue);
    },
  }));

  return columns;
}
